import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import { createAsset } from '../../services/VideoGenService';

import CreateAssetFileSelect from './CreateAssetFileSelect';

const ASSET_DROPZONE_ACCEPT = {
  'video/*': ['.mp4', '.mov', '.avi', '.wmv'],
  'audio/*': ['.mp3', '.wav', '.ogg', '.m4a'],
  'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
};

interface CreateAssetModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (files: Array<File>, offerId?: string) => void;
  onUploadComplete: () => void;
}

interface FileProgress {
  file: File;
  progress: number;
  assetId?: string;
}

export default function CreateAssetModal({ open, onClose, onUploadComplete }: CreateAssetModalProps) {
  const [files, setFiles] = useState<Array<File>>([]);
  const [fileProgress, setFileProgress] = useState<Array<FileProgress>>([]);
  const [offerId, setOfferId] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState<{
    message: string;
    errors?: Array<{ message: string; path: string }>;
  } | null>(null);

  const hasVideoFiles = useMemo(() => {
    return files.some((file) => file.type.startsWith('video/'));
  }, [files]);

  const onDrop = useCallback((acceptedFiles: Array<File>) => {
    setFiles(acceptedFiles);
    setFileProgress(acceptedFiles.map((file) => ({ file, progress: 0 })));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ASSET_DROPZONE_ACCEPT,
    multiple: true,
  });

  const handleUpload = async () => {
    try {
      if (hasVideoFiles && !offerId) {
        setError({
          message: 'Offer ID is required when uploading video files',
        });
        return;
      }
      setError(null);
      setIsUploading(true);
      setIsUploadComplete(false);
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const response = await createAsset(file, offerId || undefined, file.name, (progress) => {
          setFileProgress((prev) => {
            const newProgress = [...prev];
            newProgress[i] = { ...newProgress[i], progress };
            return newProgress;
          });
        });
        const assetId = response.result.id;
        setFileProgress((prev) => {
          const newProgress = [...prev];
          newProgress[i] = { ...newProgress[i], progress: 100, assetId };
          return newProgress;
        });
      }
      setIsUploadComplete(true);
    } catch (error) {
      console.error('Failed to upload files:', error);
      enqueueSnackbar('Failed to upload files', { variant: 'error' });
      const apiError = error as Error;
      setError({
        message: apiError.message || 'Failed to upload files',
        errors: [],
      });
    } finally {
      setIsUploading(false);
      onUploadComplete();
    }
  };

  const handleNewUpload = () => {
    setFiles([]);
    setFileProgress([]);
    setOfferId('');
    setIsUploadComplete(false);
    setError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 4 }}>
        Upload Assets
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            p: 3,
            mb: 2,
            textAlign: 'center',
            cursor: isUploading || isUploadComplete ? 'not-allowed' : 'pointer',
          }}
        >
          <input {...getInputProps()} disabled={isUploading} />
          <Typography>
            {isDragActive
              ? 'Drop the files here...'
              : files.length > 0
                ? `${files.length} file(s) selected`
                : 'Drop or select files'}
          </Typography>
        </Box>
        {files.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {files.map((file, idx) => (
              <CreateAssetFileSelect
                key={file.name + file.size + idx}
                file={file}
                progress={fileProgress[idx]}
                isUploading={isUploading}
                onRemove={() => setFiles(files.filter((_, i) => i !== idx))}
              />
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TextField
            label="offerId"
            size="small"
            value={offerId}
            onChange={(e) => setOfferId(e.target.value)}
            helperText={hasVideoFiles ? 'Required when uploading video files' : 'Optional'}
            placeholder="input"
            fullWidth
            disabled={isUploading}
            required={hasVideoFiles}
            error={hasVideoFiles && !offerId}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', gap: 2 }}>
        {error && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {error.message}
            {error.errors?.map((err, index) => (
              <Typography key={index} variant="body2" sx={{ mt: 0.5 }}>
                {err.message}
              </Typography>
            ))}
          </Alert>
        )}
        <Button
          variant="contained"
          fullWidth
          onClick={isUploadComplete ? handleNewUpload : handleUpload}
          disabled={files.length === 0 || (isUploading && !isUploadComplete)}
        >
          {isUploadComplete ? 'New Upload' : isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
